import { Link } from "react-router-dom";
import { ShieldCheck, ChartBar, FileText, ClipboardText, Lock, ArrowRight } from "@phosphor-icons/react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1527576539890-dfa815648363?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBjaGFyY29hbHxlbnwwfHx8fDE3ODA0NzMxOTd8MA&ixlib=rb-4.1.0&q=85";

const EXEC_IMG =
    "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxleGVjdXRpdmUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBkYXJrfGVufDB8fHx8MTc4MDQ3MzE5N3ww&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
    { label: "License Visibility", code: "01" },
    { label: "User Segmentation", code: "02" },
    { label: "Governance", code: "03" },
    { label: "Cost Control", code: "04" },
    { label: "Future Readiness", code: "05" },
    { label: "Procurement Readiness", code: "06" },
];

// Callout-label chip: mirrors the .callout-label / .callout-label-* classes used inside the
// AI report so the Landing page's "what each scan produces" description is visually consistent
// with the actual report artefact the consultant hands to the client.
const CALLOUT_CHIPS = [
    { label: "FACT", cls: "callout-label-fact" },
    { label: "ASSUMPTION", cls: "callout-label-assumption" },
    { label: "RISK", cls: "callout-label-risk" },
    { label: "OPPORTUNITY", cls: "callout-label-opportunity" },
];

export default function Landing() {
    return (
        <div data-testid="landing-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-[#2a2a2a]">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.95) 100%), url(${HERO_IMG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-8 fade-up">
                        <div className="overline mb-6" data-testid="hero-overline">
                            Independent Microsoft Licensing Assessments — For Consultants
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                            Run advisor-grade
                            <br />
                            <span className="text-[#00A896]">licensing scans</span>{" "}
                            for the clients
                            <br />
                            sitting across the table.
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-[#c2c2c2] max-w-2xl leading-relaxed">
                            A confidential workshop tool for independent advisors. Sit
                            with your CIO, IT Director or Procurement lead, work
                            through six dimensions of Microsoft licensing maturity,
                            and produce a board-ready brief in the language their
                            procurement office already speaks. You keep the
                            commentary — the tool does the structuring.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/register"
                                className="btn-primary inline-flex items-center gap-3"
                                data-testid="hero-start-button"
                            >
                                Start a Scan for a Client <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link to="/login" className="btn-secondary" data-testid="hero-signin-button">
                                I already have an account
                            </Link>
                        </div>
                        <div className="mt-10 text-xs uppercase tracking-[0.12em] text-[#9a9a9a]">
                            Not Microsoft · Not a reseller · No vendor recommendations
                        </div>
                    </div>

                    <div className="hidden md:flex col-span-4 flex-col gap-3 fade-up">
                        <div className="border border-[#2a2a2a] bg-[#141414] p-5">
                            <div className="overline mb-3">What each scan produces</div>
                            <ul className="space-y-2 text-sm text-[#d4d4d4]">
                                <li>· Executive Summary written for the client&apos;s CIO</li>
                                <li>· Red / Amber / Green ratings across six dimensions</li>
                                <li>· E1 vs E3 planning considerations</li>
                                <li>· RFP preparation checklist</li>
                                <li>· Information gaps to close before procurement</li>
                            </ul>
                            <div className="mt-4 pt-3 border-t border-[#2a2a2a]">
                                <div className="text-[11px] uppercase tracking-[0.12em] text-[#9a9a9a] mb-2">
                                    Every finding is tagged
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="hero-callout-chips">
                                    {CALLOUT_CHIPS.map((c) => (
                                        <span key={c.label} className={`callout-label ${c.cls}`}>
                                            {c.label}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-4 pt-3 border-t border-[#2a2a2a] text-[11px] uppercase tracking-[0.12em] text-[#9a9a9a]">
                                Downloadable as PDF · Editable as Word .docx
                            </div>
                        </div>
                        <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-5 flex items-center gap-3">
                            <Lock size={18} className="text-[#00A896]" />
                            <span className="text-xs text-[#c2c2c2] leading-relaxed">
                                Every client&apos;s data is yours to erase. GDPR one-click delete on every scan.
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
                    <div className="grid grid-cols-12 gap-8">
                        <div className="col-span-12 md:col-span-4">
                            <div className="overline mb-4">Methodology</div>
                            <h2 className="font-display text-3xl md:text-4xl leading-tight">
                                Six dimensions. One executive view.
                            </h2>
                            <p className="text-[#c2c2c2] mt-6 leading-relaxed">
                                Each scan evaluates the six dimensions your client&apos;s
                                procurement office, CIO and CFO already care about —
                                and surfaces the gaps that typically delay or
                                distort Microsoft renewals.
                            </p>
                        </div>
                        <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            {CATEGORIES.map((c) => (
                                <div
                                    key={c.code}
                                    className="bg-[#0a0a0a] p-6 hover:bg-[#141414] transition-colors"
                                >
                                    <div className="font-mono text-[13px] text-[#9a9a9a]">
                                        {c.code}
                                    </div>
                                    <div className="font-display text-xl mt-2 text-white">
                                        {c.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-7">
                        <div className="overline mb-6">How it works</div>
                        <h2 className="font-display text-3xl md:text-5xl leading-tight">
                            From workshop conversation to board-ready brief in minutes.
                        </h2>
                        <div className="mt-10 space-y-8">
                            {[
                                {
                                    icon: ClipboardText,
                                    title: "Work through the questionnaire with the client",
                                    body: "Six categories, plain-English statements. Upload their license exports, strategy docs and workshop notes as evidence.",
                                },
                                {
                                    icon: ChartBar,
                                    title: "Ratings surface as you go",
                                    body: "Each dimension scores automatically. Red / Amber / Green plus a maturity level per category.",
                                },
                                {
                                    icon: FileText,
                                    title: "Generate the executive report",
                                    body: "An advisor-grade narrative for the client\u2019s CIO and procurement team. Every finding tagged FACT · ASSUMPTION · RISK · OPPORTUNITY.",
                                },
                                {
                                    icon: ShieldCheck,
                                    title: "Hand it over with confidence",
                                    body: "Download PDF or Word. Add your own commentary. The client goes into their next Microsoft conversation better prepared.",
                                },
                            ].map((s, i) => (
                                <div key={s.title} className="flex gap-5">
                                    <div className="shrink-0 w-12 h-12 border border-[#2a2a2a] flex items-center justify-center bg-[#141414]">
                                        <s.icon size={20} className="text-[#00A896]" />
                                    </div>
                                    <div>
                                        <div className="font-mono text-[13px] text-[#9a9a9a]">
                                            STEP {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className="font-display text-xl mt-1">
                                            {s.title}
                                        </div>
                                        <p className="text-[#c2c2c2] mt-1 leading-relaxed text-sm md:text-base">
                                            {s.body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-5">
                        <div
                            className="aspect-[4/5] bg-cover bg-center border border-[#2a2a2a]"
                            style={{ backgroundImage: `url(${EXEC_IMG})` }}
                        />
                        <div className="mt-4 text-xs uppercase tracking-[0.12em] text-[#9a9a9a]">
                            The report is designed to sit alongside your senior-management commentary.
                        </div>
                    </div>
                </div>
            </section>

            {/* GDPR / PRIVACY */}
            <section className="border-b border-[#2a2a2a] bg-[#0a0a0a]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-8 items-start">
                    <div className="col-span-12 md:col-span-5">
                        <div className="overline mb-4">Client confidentiality, by design</div>
                        <h2 className="font-display text-3xl md:text-4xl leading-tight">
                            GDPR-level privacy. Every scan, every client.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {[
                            ["Right to access", "Every scan you have run is listed in your dashboard for as long as you keep it."],
                            ["Right to erasure", "One click deletes an assessment — evidence, answers, and generated report — permanently."],
                            ["No vendor sharing", "Client responses are never shared with Microsoft, resellers, or third parties."],
                            ["Confidential by default", "Each scan is visible only to you and the platform admin for support purposes."],
                        ].map(([t, b]) => (
                            <div key={t} className="bg-[#0a0a0a] p-6">
                                <div className="font-mono text-[13px] text-[#9a9a9a] uppercase tracking-widest">
                                    {t}
                                </div>
                                <div className="mt-2 text-[#d4d4d4] leading-relaxed">{b}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-center">
                    <div className="overline mb-4">Begin</div>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
                        Your next client conversation deserves a structured, defensible starting point.
                    </h2>
                    <div className="mt-10">
                        <Link
                            to="/register"
                            className="btn-primary inline-flex items-center gap-3"
                            data-testid="cta-start-button"
                        >
                            Start a Scan for a Client <ArrowRight size={16} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
